import { Alert, Button, Group, Modal, Textarea } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { DayInput } from '../../inputs/DayInput.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Drydock.module.css';
import { EXECUTION_TONE } from './ExecutionPanels.js';
import { useDrydockErrorText } from './errors.js';
import { DrydockReportButtons } from './ReportButtons.js';
import type { ReportMode } from './report-export.js';
import type { DrydockProjectSource } from './source.js';
import type { DrydockCloseout, DrydockCloseoutJob, DrydockProject } from './types.js';

/**
 * Dock in / Undock ve proje kapanış sihirbazı (ADR 0129 Ek Faz 4; Excel Screen 12 "Close
 * Project"). Kapanış tek transaction'dır: ofisçe doğrulanan işler Closed, yapılmayanlar Pending
 * olur; açık denetim kapanışı engeller. Gün gemi günüdür; gelecek gün sunucuda reddedilir.
 */

/** Dock in ya da Undock günü (Master, Chief Engineer; gün sonra değişmez). */
export function DockModal({
  project,
  action,
  source,
  onClose,
  onDone,
}: {
  project: DrydockProject;
  action: 'dock_in' | 'undock';
  source: DrydockProjectSource;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [on, setOn] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dockIn = action === 'dock_in';

  async function save() {
    if (!on || !source.dock) return;
    setBusy(true);
    setError(null);
    try {
      await source.dock({ id: project.id, rowVersion: project.rowVersion, action, on });
      await onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={t(dockIn ? 'drydock.yard.dockInTitle' : 'drydock.yard.undockTitle', {
        no: project.projectNo,
      })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>
          {t(dockIn ? 'drydock.yard.dockHint' : 'drydock.yard.undockHint')}
        </p>
        {error ? <Alert color="red">{error}</Alert> : null}
        <DayInput
          label={t('drydock.yard.date')}
          required
          value={on}
          minDate={dockIn ? undefined : (project.dockedOn ?? undefined)}
          onChange={setOn}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={busy} disabled={on === null} onClick={() => void save()}>
            {t(dockIn ? 'drydock.yard.dockIn' : 'drydock.yard.undock')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

function JobGroup({ title, rows }: { title: string; rows: readonly DrydockCloseoutJob[] }) {
  const { t } = useTranslation();
  return (
    <section>
      <h4 className={classes.groupTitle}>
        {title} ({rows.length})
      </h4>
      {rows.length === 0 ? (
        <p className={classes.muted}>{t('drydock.yard.none')}</p>
      ) : (
        <ul className={classes.inspections}>
          {rows.map((j) => (
            <li key={j.id}>
              <span className={classes.mono}>{j.jobNo}</span>
              <span>{j.title}</span>
              <StatusBadge tone={EXECUTION_TONE[j.state]}>
                {t(`drydock.yard.executionStates.${j.state}`)}
              </StatusBadge>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Kapanış sihirbazı: önizleme, Undock günü, not ve onay (ayar açıksa parola). */
export function CloseoutModal({
  project,
  source,
  report,
  onClose,
  onDone,
}: {
  project: DrydockProject;
  source: DrydockProjectSource;
  /** Faz 5: önizlemenin kapanış raporu ("PREVIEW – PROJECT NOT CLOSED"). */
  report?: { run: (mode: ReportMode) => Promise<string | null>; busy: boolean } | undefined;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const reauth = useReauth('drydock_close');
  const [preview, setPreview] = useState<DrydockCloseout | null>(null);
  const [undockedOn, setUndockedOn] = useState<string | null>(project.undockedOn ?? null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ship = Boolean(source.close);

  useEffect(() => {
    let alive = true;
    if (!source.closeout) return;
    source
      .closeout(project.id)
      .then((value) => {
        if (alive) setPreview(value);
      })
      .catch((err) => {
        if (alive) setError(errorText(err));
      });
    return () => {
      alive = false;
    };
  }, [source, project.id, errorText]);

  const blocked = (preview?.openInspections.length ?? 0) > 0;

  async function save() {
    if (!source.close || !undockedOn) return;
    setBusy(true);
    setError(null);
    try {
      await source.close({
        id: project.id,
        rowVersion: project.rowVersion,
        undockedOn,
        note: note.trim() || null,
        ...reauth.body(),
      });
      await onDone();
    } catch (err) {
      reauth.noteError(err);
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={t('drydock.yard.closeTitle', { no: project.projectNo })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{t('drydock.yard.closeHint')}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        {preview ? (
          <>
            {blocked ? (
              <Alert color="yellow">
                {t('drydock.yard.closeBlocked', { list: preview.openInspections.join(', ') })}
              </Alert>
            ) : null}
            {preview.awaitingVerification.length > 0 ? (
              <Alert color="yellow">
                {t('drydock.yard.awaitingVerification', {
                  list: preview.awaitingVerification.join(', '),
                })}
              </Alert>
            ) : null}
            <JobGroup title={t('drydock.yard.closeGroups.close')} rows={preview.close} />
            <JobGroup title={t('drydock.yard.closeGroups.pending')} rows={preview.pending} />
            <JobGroup
              title={t('drydock.yard.closeGroups.alreadyPending')}
              rows={preview.alreadyPending}
            />
            <JobGroup title={t('drydock.yard.closeGroups.untouched')} rows={preview.untouched} />
            {preview.openFindings.length > 0 ? (
              <p className={classes.muted}>
                {t('drydock.yard.openFindings', { list: preview.openFindings.join(', ') })}
              </p>
            ) : null}
          </>
        ) : null}
        {ship ? (
          <>
            <DayInput
              label={t('drydock.yard.undockedOn')}
              required
              value={undockedOn}
              minDate={project.dockedOn ?? undefined}
              onChange={setUndockedOn}
            />
            <Textarea
              label={t('drydock.yard.closeNote')}
              autosize
              minRows={2}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
            />
            <ReauthField reauth={reauth} label={t('drydock.scope.password')} />
          </>
        ) : null}
        {report && preview ? (
          <Group gap="xs">
            <DrydockReportButtons
              label={t('drydockReport.actions.download')}
              busy={report.busy}
              run={report.run}
            />
          </Group>
        ) : null}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t(ship ? 'drydock.cancel' : 'drydock.close')}
          </Button>
          {ship ? (
            <Button
              loading={busy}
              disabled={preview === null || blocked || !undockedOn || reauth.missing}
              onClick={() => void save()}
            >
              {t('drydock.yard.closeProject')}
            </Button>
          ) : null}
        </Group>
      </div>
    </Modal>
  );
}
